/**
 * 子證模板的「子證上限」編輯（批155，`product_plans.addon_limits_json`）。
 *
 * 形狀：`{<配額鍵>: {max?: int, max_delta?: int}}`。
 * - **有宣告的鍵＝平台可以填的鍵**；整包 `null`＝平台不能為這個模板填任何配額、只能照模板。
 * - `max`＝單張子證這一項的值上限；`max_delta`＝有取代對象（supersedes）時「新值 − 被取代那張的值」
 *   的上限（沒有取代對象時比單張的值）。超過＝平台那邊 409 `addon_requires_review`＋後台告警。
 * - 鍵必須是產品**已宣告、未停用**的配額鍵；值是 0 以上的整數；同一列兩個至少給一個
 *   （權威在後端，這裡先擋明顯的錯，免得按下儲存才看到 422）。
 *
 * 編輯器是「每個配額鍵一列、兩欄」：兩欄都空＝這一項不開放平台填。
 */
import type { AddonLimits, ProductSpec } from '@/api/ls'

export interface LimitRow {
  key: string
  label: string
  /** 畫面上的字串（空＝不設）。 */
  max: string
  max_delta: string
  /** 鍵不在憑證設計裡、或已停用——只能清空，不能存（後端會 422）。 */
  invalid: boolean
}

function str(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? String(v) : ''
}

/**
 * 憑證設計＋現值 → 編輯列。順序＝憑證設計的配額順序；已停用的鍵**只在現值有它時**才列出
 * （標 `invalid`，提示操作者清掉）；現值裡有、憑證設計卻沒有的鍵接在最後（同樣標 `invalid`）。
 */
export function limitRowsOf(spec: ProductSpec | null | undefined, limits: AddonLimits | null | undefined): LimitRow[] {
  const cur = limits ?? {}
  const rows: LimitRow[] = []
  const seen = new Set<string>()
  for (const q of spec?.quotas ?? []) {
    const v = cur[q.key]
    if (q.deprecated && !v) continue
    seen.add(q.key)
    rows.push({ key: q.key, label: q.label, max: str(v?.max), max_delta: str(v?.max_delta), invalid: q.deprecated })
  }
  for (const [k, v] of Object.entries(cur)) {
    if (seen.has(k)) continue
    rows.push({ key: k, label: k, max: str(v?.max), max_delta: str(v?.max_delta), invalid: true })
  }
  return rows
}

/** 空白＝null；其餘必須是 0 以上的整數（`'bad'`＝不合法）。 */
function parse(s: string): number | null | 'bad' {
  const t = String(s ?? '').trim()
  if (!t) return null
  return /^\d+$/.test(t) ? Number(t) : 'bad'
}

export interface BuiltLimits {
  /** 送給後端的值（沒有任何一列有值＝null）。 */
  value: AddonLimits | null
  /** 第一個問題（null＝可以存）。 */
  error: string | null
}

/** 編輯列 → `addon_limits_json`（含前端校驗）。 */
export function buildLimits(rows: LimitRow[]): BuiltLimits {
  const out: AddonLimits = {}
  for (const r of rows) {
    const max = parse(r.max)
    const delta = parse(r.max_delta)
    if (max === 'bad' || delta === 'bad') {
      return { value: null, error: `「${r.label}」的上限要填 0 以上的整數或留空。` }
    }
    if (max == null && delta == null) continue
    if (r.invalid) {
      return { value: null, error: `「${r.label}」已停用或不在憑證設計裡，不能設上限——請把那一列清空。` }
    }
    out[r.key] = {
      ...(max != null ? { max } : {}),
      ...(delta != null ? { max_delta: delta } : {}),
    }
  }
  return { value: Object.keys(out).length ? out : null, error: null }
}

/** 套裝表那一欄的摘要：`2 項開放（max／增量）`；null＝不開放平台填配額。 */
export function limitsSummary(limits: AddonLimits | null | undefined): string | null {
  const n = Object.keys(limits ?? {}).length
  return n ? `子證上限 ${n} 項` : null
}
